import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Container, Toast, TypoGraph } from 'components'
import {
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import { getAlerts, updateAlertStatus, updateAlertStatuses } from 'api'
import { AlertStatusUpdate, IAlert, IAlertFeedResponse } from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { isViewOnly } from 'tools/auth'
import { timeAgo } from 'tools/helper'
import styles from './alertFeedTab.module.scss'

const FM = strings.FLEET_MONITORING

type StatusFilter = 'all' | 'new' | 'acknowledged' | 'resolved'
type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low'
type SortField = 'alertTypeName' | 'category' | 'severity' | 'status' | 'detectedAt'

const SEVERITY_ORDER: Exclude<SeverityFilter, 'all'>[] = ['critical', 'high', 'medium', 'low']

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }
const STATUS_RANK: Record<IAlert['status'], number> = { NEW: 0, ACKNOWLEDGED: 1, RESOLVED: 2 }

const CATEGORY_PALETTE: { bg: string; color: string }[] = [
  { bg: 'rgba(37, 99, 235, 0.16)', color: '#1d4ed8' },
  { bg: 'rgba(124, 58, 237, 0.16)', color: '#6d28d9' },
  { bg: 'rgba(5, 150, 105, 0.16)', color: '#047857' },
  { bg: 'rgba(217, 119, 6, 0.18)', color: '#b45309' },
  { bg: 'rgba(8, 145, 178, 0.16)', color: '#0e7490' },
  { bg: 'rgba(219, 39, 119, 0.16)', color: '#be185d' },
  { bg: 'rgba(202, 138, 4, 0.18)', color: '#a16207' },
  { bg: 'rgba(79, 70, 229, 0.16)', color: '#4338ca' },
]

const severityLabel = (severity: string): string => {
  switch (severity) {
    case 'critical': return FM.COMMON.CRITICAL
    case 'high': return FM.COMMON.HIGH
    case 'medium': return FM.COMMON.MEDIUM
    case 'low': return FM.COMMON.LOW
    default: return severity
  }
}

const severityClass = (severity: string | null): string => {
  switch ((severity || '').toLowerCase()) {
    case 'critical': return styles.severityCritical
    case 'high': return styles.severityHigh
    case 'medium': return styles.severityMedium
    case 'low': return styles.severityLow
    default: return ''
  }
}

const rowBorderClass = (severity: string | null): string => {
  switch ((severity || '').toLowerCase()) {
    case 'critical': return styles.rowBorderCritical
    case 'high': return styles.rowBorderHigh
    case 'medium': return styles.rowBorderMedium
    case 'low': return styles.rowBorderLow
    default: return styles.rowBorderDefault
  }
}

const statusClass = (status: IAlert['status']): string => {
  switch (status) {
    case 'NEW': return styles.statusNew
    case 'ACKNOWLEDGED': return styles.statusAcknowledged
    case 'RESOLVED': return styles.statusResolved
    default: return ''
  }
}

const statusLabel = (status: IAlert['status']): string => {
  switch (status) {
    case 'NEW': return FM.ALERT_FEED.STATUS_NEW
    case 'ACKNOWLEDGED': return FM.ALERT_FEED.STATUS_ACKNOWLEDGED
    case 'RESOLVED': return FM.ALERT_FEED.STATUS_RESOLVED
    default: return status
  }
}

const AlertFeedTab: React.FunctionComponent = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<IAlertFeedResponse | null>(null)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null)
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set())
  const [sort, setSort] = useState<{ field: SortField | null; dir: 'asc' | 'desc' }>({
    field: 'detectedAt',
    dir: 'desc',
  })
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const readOnly = isViewOnly()

  const loadAlerts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await getAlerts()
      setData(response)
    } catch {
      // Global request interceptor already toasts the failure.
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  const rows = useMemo(() => data?.rows || [], [data])

  const categories = useMemo(() => {
    const set = new Set<string>()
    rows.forEach(row => {
      if (row.category) set.add(row.category)
    })
    return Array.from(set).sort()
  }, [rows])

  const categoryColorMap = useMemo(() => {
    const map = new Map<string, { bg: string; color: string }>()
    categories.forEach((category, i) => {
      map.set(category, CATEGORY_PALETTE[i % CATEGORY_PALETTE.length])
    })
    return map
  }, [categories])

  const categoryChipStyle = (category: string): React.CSSProperties => {
    const palette = categoryColorMap.get(category) || CATEGORY_PALETTE[0]
    return { backgroundColor: palette.bg, color: palette.color }
  }

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(row => {
      if (statusFilter !== 'all' && row.status.toLowerCase() !== statusFilter) return false
      if (severityFilter !== 'all' && (row.severity || '').toLowerCase() !== severityFilter) return false
      if (categoryFilter !== 'all' && row.category !== categoryFilter) return false
      if (!q) return true
      return (
        (row.fleet || '').toLowerCase().includes(q) ||
        (row.vin || '').toLowerCase().includes(q) ||
        (row.alertTypeName || '').toLowerCase().includes(q) ||
        (row.detailText || '').toLowerCase().includes(q)
      )
    })
  }, [rows, query, statusFilter, severityFilter, categoryFilter])

  const sortValue = (alert: IAlert, field: SortField): number | string => {
    switch (field) {
      case 'alertTypeName': return (alert.alertTypeName || '').toLowerCase()
      case 'category': return (alert.category || '').toLowerCase()
      case 'severity': return SEVERITY_RANK[(alert.severity || '').toLowerCase()] ?? 99
      case 'status': return STATUS_RANK[alert.status] ?? 99
      case 'detectedAt': return alert.detectedAt ? new Date(alert.detectedAt).getTime() : 0
      default: return 0
    }
  }

  const sortedRows = useMemo(() => {
    const { field, dir } = sort
    if (!field) return filteredRows
    const multiplier = dir === 'asc' ? 1 : -1
    return [...filteredRows].sort((a, b) => {
      const av = sortValue(a, field)
      const bv = sortValue(b, field)
      if (av < bv) return -multiplier
      if (av > bv) return multiplier
      return 0
    })
  }, [filteredRows, sort])

  const toggleSort = (field: SortField) =>
    setSort(prev => (
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    ))

  const renderSortHeader = (field: SortField, label: string) => (
    <button type="button" className={styles.sortHeader} onClick={() => toggleSort(field)}>
      {label}
      {sort.field === field && (
        <span className={styles.sortArrow}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  )

  const toggleRow = (id: number) =>
    setExpandedRowId(prev => (prev === id ? null : id))

  const toggleSelect = (id: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allVisibleSelected = sortedRows.length > 0 && sortedRows.every(row => selectedIds.has(row.id))
  const someVisibleSelected = !allVisibleSelected && sortedRows.some(row => selectedIds.has(row.id))

  const toggleSelectAllVisible = () =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allVisibleSelected) sortedRows.forEach(row => next.delete(row.id))
      else sortedRows.forEach(row => next.add(row.id))
      return next
    })

  const clearSelection = () => setSelectedIds(new Set())

  const selectedAlerts = useMemo(() => rows.filter(row => selectedIds.has(row.id)), [rows, selectedIds])
  const ackEligibleIds = useMemo(
    () => selectedAlerts.filter(row => row.status === 'NEW').map(row => row.id),
    [selectedAlerts]
  )
  const resolveEligibleIds = useMemo(
    () => selectedAlerts.filter(row => row.status !== 'RESOLVED').map(row => row.id),
    [selectedAlerts]
  )

  const updateStatus = async (id: number, status: AlertStatusUpdate) => {
    setBusyIds(prev => new Set(prev).add(id))
    try {
      await updateAlertStatus(id, status)
      Toast({
        type: 'success',
        subTitle: status === 'acknowledged' ? FM.TOASTS.ALERT_ACKNOWLEDGED : FM.TOASTS.ALERT_RESOLVED,
      })
      await loadAlerts(true)
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.UPDATE_ALERT_STATUS_FAILED })
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const bulkUpdate = async (status: AlertStatusUpdate) => {
    const ids = status === 'acknowledged' ? ackEligibleIds : resolveEligibleIds
    if (ids.length === 0) return
    setBulkBusy(true)
    try {
      await updateAlertStatuses(ids, status)
      Toast({ type: 'success', subTitle: FM.TOASTS.ALERTS_UPDATED })
      await loadAlerts(true)
      clearSelection()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.UPDATE_ALERT_STATUS_FAILED })
    } finally {
      setBulkBusy(false)
    }
  }

  if (loading) {
    return (
      <Container className={styles.loader}>
        <CircularProgress />
      </Container>
    )
  }

  const summary = data?.summary
  const statusCounts = data?.statusCounts

  return (
    <div data-testid="fleet-monitoring-alerts-body">
      <div className={styles.statCards}>
        <div className={`${styles.statCard} ${styles.statCardCritical}`}>
          <div className={styles.statValue} data-testid="stat-critical">{summary?.critical ?? 0}</div>
          <div className={styles.statLabel}>{FM.ALERT_FEED.CARD_CRITICAL}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardHigh}`}>
          <div className={styles.statValue} data-testid="stat-high">{summary?.high ?? 0}</div>
          <div className={styles.statLabel}>{FM.ALERT_FEED.CARD_HIGH}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardOpen}`}>
          <div className={styles.statValue} data-testid="stat-open">{summary?.open ?? 0}</div>
          <div className={styles.statLabel}>{FM.ALERT_FEED.CARD_OPEN}</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardResolvedToday}`}>
          <div className={styles.statValue} data-testid="stat-resolved-today">{summary?.resolvedToday ?? 0}</div>
          <div className={styles.statLabel}>{FM.ALERT_FEED.CARD_RESOLVED_TODAY}</div>
        </div>
      </div>

      <Paper elevation={0} sx={{ border: '1px solid #E9E9E9', borderRadius: '8px', overflow: 'hidden' }}>
        <div className={styles.toolbar}>
          <TextField
            size="small"
            placeholder={FM.ALERT_FEED.SEARCH_PLACEHOLDER}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={styles.search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <div className={styles.filterChips}>
            <FilterChip
              label={FM.ALERT_FEED.STATUS_ALL}
              count={statusCounts?.all ?? 0}
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            />
            <FilterChip
              label={FM.ALERT_FEED.STATUS_NEW}
              count={statusCounts?.new ?? 0}
              active={statusFilter === 'new'}
              onClick={() => setStatusFilter('new')}
            />
            <FilterChip
              label={FM.ALERT_FEED.STATUS_ACKNOWLEDGED}
              count={statusCounts?.acknowledged ?? 0}
              active={statusFilter === 'acknowledged'}
              onClick={() => setStatusFilter('acknowledged')}
            />
            <FilterChip
              label={FM.ALERT_FEED.STATUS_RESOLVED}
              count={statusCounts?.resolved ?? 0}
              active={statusFilter === 'resolved'}
              onClick={() => setStatusFilter('resolved')}
            />
          </div>
          <FormControl size="small" className={styles.selectControl}>
            <Select value={severityFilter} onChange={e => setSeverityFilter(e.target.value as SeverityFilter)}>
              <MenuItem value="all">{FM.ALERT_FEED.PRIORITY_ALL}</MenuItem>
              {SEVERITY_ORDER.map(severity => (
                <MenuItem key={severity} value={severity}>{severityLabel(severity)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className={styles.selectControl}>
            <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <MenuItem value="all">{FM.ALERT_FEED.CATEGORY_ALL}</MenuItem>
              {categories.map(category => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {!readOnly && selectedIds.size > 0 && (
          <div className={styles.bulkBar}>
            <span className={styles.bulkBarCount}>{FM.ALERT_FEED.SELECTED_COUNT(selectedIds.size)}</span>
            <div className={styles.bulkBarActions}>
              <Tooltip title={ackEligibleIds.length === 0 ? FM.ALERT_FEED.BULK_ACK_DISABLED : ''}>
                <span>
                  <Button
                    label={FM.ALERT_FEED.BULK_ACKNOWLEDGE}
                    variant="outlined"
                    onClick={() => bulkUpdate('acknowledged')}
                    disabled={bulkBusy || ackEligibleIds.length === 0}
                  />
                </span>
              </Tooltip>
              <Tooltip title={resolveEligibleIds.length === 0 ? FM.ALERT_FEED.BULK_RESOLVE_DISABLED : ''}>
                <span>
                  <Button
                    label={FM.ALERT_FEED.BULK_RESOLVE}
                    variant="outlined"
                    onClick={() => bulkUpdate('resolved')}
                    disabled={bulkBusy || resolveEligibleIds.length === 0}
                  />
                </span>
              </Tooltip>
              <Button
                label={FM.ALERT_FEED.CLEAR_SELECTION}
                variant="text"
                onClick={clearSelection}
                disabled={bulkBusy}
              />
            </div>
          </div>
        )}

        {sortedRows.length === 0 ? (
          <div className={styles.empty}>
            <TypoGraph variant="body1" content={FM.ALERT_FEED.EMPTY} />
          </div>
        ) : (
          <>
            <div className={`${styles.columnHeaders} ${readOnly ? styles.noSelect : ''}`}>
              {!readOnly && (
                <div className={styles.checkboxCell}>
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    inputProps={{ 'aria-label': 'select all visible alerts' }}
                  />
                </div>
              )}
              <div>{renderSortHeader('alertTypeName', FM.ALERT_FEED.COL_ALERT_TYPE)}</div>
              <div />
              <div>{renderSortHeader('category', FM.ALERT_FEED.COL_CATEGORY)}</div>
              <div>{renderSortHeader('severity', FM.ALERT_FEED.COL_SEVERITY)}</div>
              <div>{renderSortHeader('status', FM.ALERT_FEED.COL_STATUS)}</div>
              <div className={styles.detectedHeader}>{renderSortHeader('detectedAt', FM.ALERT_FEED.COL_DETECTED)}</div>
              <div />
            </div>
            <div className={styles.rowsScroll}>
              {sortedRows.map(alert => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  expanded={expandedRowId === alert.id}
                  busy={busyIds.has(alert.id)}
                  selected={selectedIds.has(alert.id)}
                  readOnly={readOnly}
                  categoryChipStyle={categoryChipStyle}
                  onToggle={() => toggleRow(alert.id)}
                  onToggleSelect={() => toggleSelect(alert.id)}
                  onAcknowledge={() => updateStatus(alert.id, 'acknowledged')}
                  onResolve={() => updateStatus(alert.id, 'resolved')}
                />
              ))}
            </div>
          </>
        )}
      </Paper>
    </div>
  )
}

interface IFilterChipProps {
  label: string
  count: number
  active: boolean
  onClick: () => void
}

const FilterChip: React.FC<IFilterChipProps> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${styles.filterChip} ${active ? styles.filterChipActive : ''}`}
  >
    {label}
    <span className={styles.filterChipCount}>{count}</span>
  </button>
)

interface IAlertRowProps {
  alert: IAlert
  expanded: boolean
  busy: boolean
  selected: boolean
  readOnly: boolean
  categoryChipStyle: (category: string) => React.CSSProperties
  onToggle: () => void
  onToggleSelect: () => void
  onAcknowledge: () => void
  onResolve: () => void
}

const AlertRow: React.FC<IAlertRowProps> = ({
  alert,
  expanded,
  busy,
  selected,
  readOnly,
  categoryChipStyle,
  onToggle,
  onToggleSelect,
  onAcknowledge,
  onResolve,
}) => {
  const secondaryLine = alert.vin ? `${alert.fleet || '—'} · ${alert.vin}` : (alert.fleet || '—')

  return (
    <div className={`${styles.rowWrapper} ${rowBorderClass(alert.severity)}`}>
      <div
        className={`${styles.tableRow} ${readOnly ? styles.noSelect : ''} ${expanded ? styles.tableRowExpanded : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        {!readOnly && (
          <div className={styles.checkboxCell} onClick={e => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={selected}
              onChange={onToggleSelect}
              inputProps={{ 'aria-label': `select alert ${alert.id}` }}
            />
          </div>
        )}

        <div>
          <div className={styles.alertTypeName}>{alert.alertTypeName}</div>
          <div className={styles.secondaryLine}>{secondaryLine}</div>
        </div>

        <div />

        <div>
          {alert.category ? (
            <span className={styles.categoryChip} style={categoryChipStyle(alert.category)}>
              {alert.category}
            </span>
          ) : (
            <span className={styles.dash}>—</span>
          )}
        </div>

        <div>
          {alert.severity ? (
            <span className={severityClass(alert.severity)}>{severityLabel(alert.severity)}</span>
          ) : (
            <span className={styles.dash}>—</span>
          )}
        </div>

        <div>
          <span className={`${styles.statusPill} ${statusClass(alert.status)}`}>
            {statusLabel(alert.status)}
          </span>
        </div>

        <div className={styles.detectedCell}>{timeAgo(alert.detectedAt)}</div>

        <div className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}>
          <KeyboardArrowDownIcon fontSize="small" />
        </div>
      </div>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={styles.expandedRow}>
          <p className={styles.detailText}>{alert.detailText}</p>
          {alert.chips.length > 0 && (
            <div className={styles.chipsRow}>
              {alert.chips.map((chip, idx) => (
                <span key={`${chip.label}-${idx}`} className={styles.metaChip}>
                  {chip.label}: {chip.value}
                </span>
              ))}
            </div>
          )}
          {!readOnly && (
            <div className={styles.detailFooter}>
              <div className={styles.actionsRow} onClick={e => e.stopPropagation()}>
                {alert.status === 'NEW' && (
                  <Button
                    label={FM.ALERT_FEED.ACKNOWLEDGE}
                    variant="outlined"
                    size="small"
                    onClick={onAcknowledge}
                    disabled={busy}
                  />
                )}
                {alert.status !== 'RESOLVED' && (
                  <Button
                    label={FM.ALERT_FEED.RESOLVE}
                    variant="contained"
                    size="small"
                    onClick={onResolve}
                    disabled={busy}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default AlertFeedTab
